import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { isDTS } from "../../utility/platform";
import { getGeoStatus } from "../actions/geo";
import WebsiteBrandLogo from "../common/WebsiteBrandLogo";
import { getExcludedStates, isExcludedState } from "../../utility/complianceCopy";
import "../../style/compliance.css";
// The .geo-block* rules live here. GeoGate has rendered these class names since
// Stage 1, but nothing in the app defined them — the block screen was rendering
// as unstyled text rather than a full-screen interstitial.
import "../../style/dts-ui.css";

const GEO_KEY = "dtsGeoState";

/**
 * DTS geofencing gate.
 *
 * Stage 1 built this screen and resolved the visitor's location in the browser,
 * from a query parameter, with a note that real server-side geolocation would
 * come later. This is later: the decision now comes from GET /api/geo/status,
 * which resolves the state from the request's IP and applies the admin-
 * configured excluded list server-side.
 *
 * WHAT THIS COMPONENT IS AND IS NOT
 * ---------------------------------
 * It is the *presentation* of a block, not the enforcement of one. Anyone can
 * open devtools and delete this element. Enforcement lives on the server, on
 * the routes that matter — registration and every money-moving endpoint — where
 * the client cannot reach it. This screen exists so an excluded visitor is told
 * clearly rather than discovering it at the moment they try to redeem.
 *
 * Two behaviors are deliberate:
 *
 *  - While the check is in flight, the app shell stays hidden behind a neutral
 *    loading state. This prevents excluded visitors from seeing the site before
 *    the server's decision arrives. A block still follows only a definite
 *    excluded-state answer.
 *
 *  - A failed check renders the site. The server's money gate fails closed on
 *    its own; a blip on this endpoint must not black-screen the product.
 */
export default function GeoGate({ children }) {
  const location = useLocation();
  const [override, setOverride] = useState(
    () => (typeof sessionStorage !== "undefined" && sessionStorage.getItem(GEO_KEY)) || ""
  );
  const [serverState, setServerState] = useState(null);
  const [geoCheckComplete, setGeoCheckComplete] = useState(false);

  // Demo/test hook, NON-PRODUCTION ONLY. `?geo=WA` forces the block screen so
  // the gate can be demonstrated without arranging traffic from an excluded
  // state. A test hook that survives to production is a bypass, so it is
  // compiled against NODE_ENV rather than merely discouraged.
  const devHookEnabled = process.env.NODE_ENV !== "production";

  useEffect(() => {
    if (!devHookEnabled) return;
    const params = new URLSearchParams(location.search);
    if (!params.has("geo")) return;
    const val = String(params.get("geo") || "").toUpperCase();
    if (!val || val === "CLEAR" || val === "US") {
      sessionStorage.removeItem(GEO_KEY);
      setOverride("");
    } else {
      sessionStorage.setItem(GEO_KEY, val);
      setOverride(val);
    }
  }, [location.search, devHookEnabled]);

  useEffect(() => {
    let cancelled = false;
    if (!isDTS()) return undefined;

    (async () => {
      try {
        const res = await getGeoStatus();
        if (cancelled) return;
        const result = res?.result;
        if (result && typeof result === "object") {
          setServerState(result);
          // Kept for the rest of the app (and for anything still reading the
          // Stage 1 hook) rather than for this component's own decision.
          if (typeof window !== "undefined") {
            window.__DTS_GEO_STATE__ = result.state || "";
          }
        }
      } catch {
        // Deliberately silent: after the check completes, render the site.
      } finally {
        if (!cancelled) setGeoCheckComplete(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!isDTS()) return children;

  // The dev override wins when present, so the block can be demonstrated
  // without a real excluded-state IP.
  if (devHookEnabled && override && isExcludedState(override)) {
    return <GeoBlockScreen stateCode={override} />;
  }

  if (serverState && serverState.allowed === false) {
    return <GeoBlockScreen stateCode={serverState.state} />;
  }

  if (!geoCheckComplete) return <GeoCheckingScreen />;

  return children;
}

function GeoCheckingScreen() {
  return (
    <main className="geo-checking" role="status" aria-live="polite" aria-label="Loading">
      <WebsiteBrandLogo className="geo-checking__logo" asLink={false} />
      <span className="geo-checking__spinner" aria-hidden="true" />
      <span className="geo-checking__label">Loading…</span>
    </main>
  );
}

function GeoBlockScreen({ stateCode }) {
  const stateName =
    getExcludedStates().find((s) => s.code === stateCode)?.name || stateCode;
  return (
    <main className="geo-block">
      <section
        className="geo-block__card"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        aria-labelledby="jurisdiction-title"
        aria-describedby="jurisdiction-body"
      >
        <div className="geo-block__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" focusable="false">
            <path d="M12 3 20 6.5v5.1c0 4.4-2.8 7.6-8 9.9-5.2-2.3-8-5.5-8-9.9V6.5L12 3Z" />
            <path d="M12 8v5m0 3h.01" />
          </svg>
        </div>
        <p className="geo-block__eyebrow">Jurisdiction notice</p>
        <h1 className="geo-block__title" id="jurisdiction-title">
          Access unavailable in {stateName}
        </h1>
        <p className="geo-block__body" id="jurisdiction-body">
          This promotional sweepstakes is not available in this jurisdiction and
          is void where prohibited by law.
        </p>
      </section>
    </main>
  );
}
